// useStudioViewport — the Studio canvas's own listeners (Studio.tsx): the
// wheel and a focus scroll pan or zoom the view, a resize keeps its centre,
// and the view is kept for the next visit whenever the Studio is left: on
// unmount (another tab), and when the page is hidden or unloaded (a reload
// or a closed tab never runs React's cleanups, #115). Each listener is on the
// canvas element or the page itself and only reports up (`setView`,
// `onLeave`); nothing here decides anything.

export { useStudioViewport };

import {
    type Dispatch,
    type RefObject,
    type SetStateAction,
    useEffect,
    useRef,
} from "react";
import {
    keepCentre,
    type Size,
    type StoredView,
    type StudioView,
    storedViewOf,
    wheelZoom,
    zoomAround,
} from "./studioLayout.js";

type ViewportDeps = {
    readonly viewport: RefObject<HTMLDivElement | null>;
    readonly view: StudioView;
    readonly setView: Dispatch<SetStateAction<StudioView>>;
    readonly sizeOf: () => Size;
    // The view was restored (before that there is nothing worth keeping).
    readonly restored: RefObject<boolean>;
    readonly onLeave: (view: StoredView) => void;
};

const useStudioViewport = ({
    viewport,
    view,
    setView,
    sizeOf,
    restored,
    onLeave,
}: ViewportDeps): void => {
    // Leaving keeps the view (zoom + the centre), read from the latest value.
    const latest = useRef({ view, size: sizeOf() });
    useEffect(() => {
        latest.current = { view, size: sizeOf() };
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: subscribes once; `onLeave` is the host's stable writer and the view is read from `latest`.
    useEffect(() => {
        const keep = () => {
            if (restored.current)
                onLeave(storedViewOf(latest.current.view, latest.current.size));
        };
        const onHidden = () => {
            if (document.visibilityState === "hidden") keep();
        };
        window.addEventListener("pagehide", keep);
        document.addEventListener("visibilitychange", onHidden);
        return () => {
            window.removeEventListener("pagehide", keep);
            document.removeEventListener("visibilitychange", onHidden);
            keep();
        };
    }, []);

    // A resized viewport keeps what was at its centre at its centre.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the viewport element and `setView` are stable for the canvas's life.
    useEffect(() => {
        const el = viewport.current;
        if (el === null) return;
        let last: Size = { width: el.clientWidth, height: el.clientHeight };
        const observer = new ResizeObserver(() => {
            const next = { width: el.clientWidth, height: el.clientHeight };
            const from = last;
            last = next;
            setView((v) => keepCentre(v, from, next));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // The wheel, on the canvas element only. React's onWheel is passive, so
    // it could not keep a pinch from zooming the page; this listener is not.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the viewport element and `setView` are stable for the canvas's life.
    useEffect(() => {
        const el = viewport.current;
        if (el === null) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            if (e.ctrlKey || e.metaKey) {
                const box = el.getBoundingClientRect();
                const at = { x: e.clientX - box.left, y: e.clientY - box.top };
                setView((v) => zoomAround(v, at, wheelZoom(v.zoom, e.deltaY)));
            } else {
                setView((v) => ({
                    zoom: v.zoom,
                    pan: { x: v.pan.x - e.deltaX, y: v.pan.y - e.deltaY },
                }));
            }
        };
        // Focusing a card off-screen (Tab) scrolls the clipped viewport
        // natively; fold that scroll into the pan so the card comes into view
        // and the pan and the cards stay in step.
        const onScroll = () => {
            const dx = el.scrollLeft;
            const dy = el.scrollTop;
            if (dx === 0 && dy === 0) return;
            el.scrollLeft = 0;
            el.scrollTop = 0;
            setView((v) => ({
                zoom: v.zoom,
                pan: { x: v.pan.x - dx, y: v.pan.y - dy },
            }));
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("scroll", onScroll);
        return () => {
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("scroll", onScroll);
        };
    }, []);
};
